import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import JSZip from 'jszip';
import { jsPDF } from 'jspdf';
import {
  Upload, Scissors, Folder, Trash2, Plus, RotateCcw,
  ChevronLeft, ChevronRight, Download, FileImage, FileText, Archive,
  RefreshCw, X, Share2, FolderPlus, Wand2, Check, GripVertical, Pencil, Save,
  ExternalLink,
} from 'lucide-react';
import RexLogo from './RexLogo';
import BoxEditor from './BoxEditor';
import TermsModal from './TermsModal';
import { detectBoxes } from '../lib/autoDetect';
import {
  loadImage, readFileAsDataURL, getImageData,
  cropRegion, canvasToBlob, canvasToJpegBlob, formatName,
} from '../lib/imageProcessor';
import { listNests, saveNest, loadNest, deleteNest, newNestId } from '../lib/storage';

const MAX_FILES = 10;
const MAX_SIZE_MB = 25;

// Detect iOS Safari (including iPadOS that masquerades as Mac).
function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes('Mac') && (navigator.maxTouchPoints || 0) > 1)
  );
}

// Detect a sandboxed / restricted iframe context (e.g. some preview viewers
// strip `allow-downloads`). When true the anchor-click trick is almost
// certainly going to be silently ignored and we should escalate to a new tab.
function isRestrictedFrame() {
  try {
    return window.self !== window.top;
  } catch (_) {
    // Cross-origin access throws — definitely in an iframe.
    return true;
  }
}

// Robust blob save that works across desktop, Android, AND iOS Safari.
//
// Strategy:
//   1. On iOS we prefer the Web Share API (opens the native share sheet so
//      "Save to Files" / "Save to Photos" works). The <a download> attribute
//      is silently ignored on iOS Safari so we never rely on it there.
//   2. If Web Share isn't available (or rejects the files), we open the blob
//      URL in a new tab so iOS Safari shows it and the user can use the
//      built-in share menu to save it manually.
//   3. On every other browser we use the standard anchor click.
//
// Returns { ok: boolean, method: 'share' | 'download' | 'tab', cancelled?: boolean }.
async function saveBlob(blob, filename) {
  // iOS path — OR any restricted iframe where the standard download trick
  // is unreliable. Both end up at the share-sheet / new-tab strategy.
  if (isIOS() || isRestrictedFrame()) {
    // Try Web Share first
    try {
      if (navigator.canShare) {
        const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: filename });
          return { ok: true, method: 'share' };
        }
      }
    } catch (e) {
      if (e && e.name === 'AbortError') {
        return { ok: false, method: 'share', cancelled: true };
      }
      console.warn('[Cropasaurus] Web Share failed, falling back to new tab', e);
    }
    // Fallback: open blob URL in a new tab so the host browser shows it and
    // the user can save it manually (long-press / share menu / right-click).
    try {
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'noopener');
      if (!w) {
        console.warn('[Cropasaurus] window.open blocked. Navigating current tab.');
        // Last resort — navigate the current tab to the blob URL
        window.location.href = url;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return { ok: true, method: 'tab' };
    } catch (e) {
      console.error('[Cropasaurus] new-tab fallback failed', e);
      return { ok: false, method: 'tab' };
    }
  }

  // Desktop & Android: standard anchor click
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      try { document.body.removeChild(a); } catch (_) { /* noop */ }
      URL.revokeObjectURL(url);
    }, 1500);
    return { ok: true, method: 'download' };
  } catch (e) {
    console.error('[Cropasaurus] saveBlob failed', e);
    return { ok: false, method: 'download' };
  }
}

export default function CropasaurusApp({ onSecretLab }) {
  // ---- core state ----
  const [sheets, setSheets] = useState([]); // {id, name, file, dataUrl, image, boxes, bg, detecting, blob}
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedBoxId, setSelectedBoxId] = useState(null);

  // ---- settings ----
  const [shape, setShape] = useState('rect');
  const [bgRemove, setBgRemove] = useState(true);
  const [tolerance, setTolerance] = useState(30);
  const [aggressive, setAggressive] = useState(false);
  const [centerInShape, setCenterInShape] = useState(false);
  const [borderEnabled, setBorderEnabled] = useState(false);
  const [borderColor, setBorderColor] = useState('#1f3d2b');
  const [borderThickness, setBorderThickness] = useState(6);
  const [bgFillEnabled, setBgFillEnabled] = useState(false);
  const [bgFillColor, setBgFillColor] = useState('#1f3d2b');
  const [applyAllSheets, setApplyAllSheets] = useState(false);

  const [prefix, setPrefix] = useState('heritage-stickers');
  const [startNum, setStartNum] = useState(1);
  const [padding, setPadding] = useState(3);

  // ---- nests ----
  const [nestList, setNestList] = useState([]);
  const [activeNestId, setActiveNestId] = useState(null);
  const [activeNestName, setActiveNestName] = useState('');
  const [newNestName, setNewNestName] = useState('');

  // ---- per-hatchling custom names + preview state ----
  const [customNames, setCustomNames] = useState({}); // {boxKey: name}
  const [previewOrder, setPreviewOrder] = useState([]); // [boxKey, ...]
  const [previews, setPreviews] = useState([]); // [{key, name, url, blob, canvas, sheetId, boxId}]
  const [selectedPreviews, setSelectedPreviews] = useState(() => new Set());

  // ---- UX state ----
  const [building, setBuilding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [savingNest, setSavingNest] = useState(false);
  const [toast, setToast] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [termsOpen, setTermsOpen] = useState(false);
  const dragKeyRef = useRef(null);
  const secretClickRef = useRef({ count: 0, t: 0 });

  function handleSecretClick() {
    const now = Date.now();
    const ref = secretClickRef.current;
    if (now - ref.t > 900) ref.count = 0;
    ref.t = now;
    ref.count += 1;
    if (ref.count >= 3) {
      ref.count = 0;
      onSecretLab && onSecretLab();
    }
  }

  const fileInputRef = useRef(null);
  const [isDragging, setDragging] = useState(false);

  // ---- toast helper ----
  function showToast(msg, kind = 'info') {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2400);
  }

  // ============ NESTS ============
  useEffect(() => { refreshNestList(); }, []);

  async function refreshNestList() {
    try { setNestList(await listNests()); } catch (e) { console.error(e); }
  }

  // ============ FILES ============
  const handleFiles = useCallback(async (files) => {
    const list = Array.from(files);
    const accepted = list
      .filter(f => /^image\/(png|jpe?g|webp)$/i.test(f.type))
      .filter(f => f.size <= MAX_SIZE_MB * 1024 * 1024);
    const tooMany = list.length > (MAX_FILES - sheets.length);
    if (list.length - accepted.length > 0) {
      showToast(`Skipped ${list.length - accepted.length} file(s). Must be PNG/JPG/WEBP and ≤ ${MAX_SIZE_MB}MB`, 'warn');
    }
    if (tooMany) {
      showToast(`Limit is ${MAX_FILES} sheets. Extras ignored`, 'warn');
    }
    const remaining = MAX_FILES - sheets.length;
    const toLoad = accepted.slice(0, remaining);

    const loaded = await Promise.all(toLoad.map(async (file) => {
      const dataUrl = await readFileAsDataURL(file);
      const image = await loadImage(dataUrl);
      const id = 's_' + Math.random().toString(36).slice(2, 9);
      return {
        id, name: file.name, file, dataUrl, image,
        boxes: [], bg: null, detecting: true, blob: file,
      };
    }));

    setSheets(prev => {
      const next = [...prev, ...loaded];
      loaded.forEach(s => detectForSheet(s.id));
      return next;
    });
  }, [sheets.length]);

  function detectForSheet(sheetId) {
    setTimeout(() => {
      setSheets(prev => {
        const s = prev.find(x => x.id === sheetId);
        if (!s) return prev;
        try {
          const { data } = getImageData(s.image);
          const result = detectBoxes(data, {
            tolerance: 28, minSizePct: 0.004, paddingPx: 6, downsample: 3,
          });
          const boxes = result.boxes.map((b, i) => ({
            id: `b_${sheetId}_${i}_${Math.random().toString(36).slice(2, 6)}`,
            x: b.x, y: b.y, w: b.w, h: b.h,
          }));
          return prev.map(x => x.id === sheetId ? { ...x, boxes, bg: result.bg, detecting: false } : x);
        } catch (e) {
          console.error('detect failed', e);
          return prev.map(x => x.id === sheetId ? { ...x, detecting: false } : x);
        }
      });
    }, 50);
  }

  function onFileInputChange(e) {
    if (e.target.files && e.target.files.length) {
      handleFiles(e.target.files);
      e.target.value = '';
    }
  }
  function onDrop(e) {
    e.preventDefault(); setDragging(false);
    if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
  }
  function onDragOver(e) { e.preventDefault(); setDragging(true); }
  function onDragLeave(e) { e.preventDefault(); setDragging(false); }

  const currentSheet = sheets[currentIdx];

  function updateBoxes(newBoxes) {
    if (!currentSheet) return;
    setSheets(prev => {
      const next = prev.map(s => s.id === currentSheet.id ? { ...s, boxes: newBoxes } : s);
      // If "apply same crop to all sheets" is on, mirror these boxes to every
      // other sheet, scaling proportionally to each target sheet's dimensions.
      if (applyAllSheets) {
        const srcW = currentSheet.image.naturalWidth;
        const srcH = currentSheet.image.naturalHeight;
        return next.map(s => {
          if (s.id === currentSheet.id) return s;
          const sx = s.image.naturalWidth / srcW;
          const sy = s.image.naturalHeight / srcH;
          return {
            ...s,
            boxes: newBoxes.map(b => ({
              id: `b_${s.id}_${b.id.slice(-6)}`,
              x: b.x * sx, y: b.y * sy, w: b.w * sx, h: b.h * sy,
            })),
          };
        });
      }
      return next;
    });
  }

  // When user FLIPS apply-all ON, immediately mirror the current sheet
  // to all other sheets (one-shot sync).
  useEffect(() => {
    if (!applyAllSheets || !currentSheet) return;
    const srcW = currentSheet.image.naturalWidth;
    const srcH = currentSheet.image.naturalHeight;
    const srcBoxes = currentSheet.boxes || [];
    setSheets(prev => prev.map(s => {
      if (s.id === currentSheet.id) return s;
      const sx = s.image.naturalWidth / srcW;
      const sy = s.image.naturalHeight / srcH;
      return {
        ...s,
        boxes: srcBoxes.map(b => ({
          id: `b_${s.id}_${b.id.slice(-6)}`,
          x: b.x * sx, y: b.y * sy, w: b.w * sx, h: b.h * sy,
        })),
      };
    }));
  }, [applyAllSheets]);

  function deleteSelectedBox() {
    if (!currentSheet || !selectedBoxId) return;
    updateBoxes(currentSheet.boxes.filter(b => b.id !== selectedBoxId));
    setSelectedBoxId(null);
  }

  function addDefaultBox() {
    if (!currentSheet) return;
    const W = currentSheet.image.naturalWidth;
    const H = currentSheet.image.naturalHeight;
    const w = Math.min(200, W * 0.2);
    const h = Math.min(200, H * 0.2);
    const id = 'b_' + Math.random().toString(36).slice(2, 9);
    const newBox = { id, x: (W - w) / 2, y: (H - h) / 2, w, h };
    updateBoxes([...(currentSheet.boxes || []), newBox]);
    setSelectedBoxId(id);
  }

  function redetectCurrent() {
    if (!currentSheet) return;
    setSheets(prev => prev.map(s => s.id === currentSheet.id ? { ...s, detecting: true, boxes: [] } : s));
    setSelectedBoxId(null);
    setTimeout(() => detectForSheet(currentSheet.id), 20);
  }

  function removeSheet(idx) {
    setSheets(prev => prev.filter((_, i) => i !== idx));
    if (currentIdx >= sheets.length - 1) setCurrentIdx(Math.max(0, sheets.length - 2));
    setPreviews([]); setSelectedPreviews(new Set());
  }

  function resetAll() {
    if (sheets.length === 0 && previews.length === 0) return;
    if (!window.confirm('Reset everything? Current sheets and previews will be cleared (saved nests stay).')) return;
    setSheets([]); setPreviews([]); setSelectedPreviews(new Set());
    setCustomNames({}); setPreviewOrder([]);
    setSelectedBoxId(null); setActiveNestId(null); setActiveNestName('');
    setCurrentIdx(0);
    showToast('Reset done. Feed the rex again');
  }

  const totalBoxes = useMemo(
    () => sheets.reduce((acc, s) => acc + (s.boxes?.length || 0), 0),
    [sheets]
  );

  // ---------- Build previews ----------
  const buildAll = useCallback(async () => {
    if (totalBoxes === 0) { setPreviews([]); return; }
    setBuilding(true);
    const built = [];
    let index = startNum;
    for (const s of sheets) {
      for (const b of (s.boxes || [])) {
        const key = `${s.id}::${b.id}`;
        const canvas = cropRegion(s.image, b, {
          shape, bgRemove, bgTolerance: tolerance, bgColor: s.bg,
          edgeFeather: 1, pad: 4, aggressive, centerInShape,
          border: borderEnabled ? { enabled: true, color: borderColor, thickness: borderThickness } : null,
          bgFill: bgFillEnabled ? bgFillColor : null,
        });
        const fallback = formatName(prefix, index, padding, startNum + totalBoxes - 1);
        const name = customNames[key] || fallback;
        const blob = await canvasToBlob(canvas, 'image/png');
        const url = URL.createObjectURL(blob);
        built.push({ key, name, url, blob, canvas, sheetId: s.id, boxId: b.id, autoName: fallback });
        index++;
      }
    }
    // honour previewOrder if present and length matches
    let ordered = built;
    if (previewOrder.length === built.length) {
      const map = new Map(built.map(p => [p.key, p]));
      const reordered = previewOrder.map(k => map.get(k)).filter(Boolean);
      if (reordered.length === built.length) ordered = reordered;
    }
    // Cleanup previous URLs
    previews.forEach(p => URL.revokeObjectURL(p.url));
    setPreviews(ordered);
    setBuilding(false);
  }, [sheets, shape, bgRemove, tolerance, aggressive, centerInShape, borderEnabled, borderColor, borderThickness, bgFillEnabled, bgFillColor, prefix, startNum, padding, totalBoxes, customNames, previewOrder]);

  useEffect(() => {
    if (totalBoxes === 0) { setPreviews([]); return; }
    const t = setTimeout(() => { buildAll(); }, 220);
    return () => clearTimeout(t);
  }, [buildAll, totalBoxes]);

  useEffect(() => () => previews.forEach(p => URL.revokeObjectURL(p.url)), [previews]);

  // When the heritage / dino mask PNG finishes loading, rebuild previews so
  // the user immediately sees the correct shape (without this, the first
  // crop after selecting that shape falls back to a circle).
  useEffect(() => {
    function onMaskLoaded() {
      if (totalBoxes > 0 && (shape === 'heritage' || shape === 'dino')) {
        buildAll();
      }
    }
    window.addEventListener('cropasaurus-mask-loaded', onMaskLoaded);
    return () => window.removeEventListener('cropasaurus-mask-loaded', onMaskLoaded);
  }, [shape, totalBoxes, buildAll]);

  // ---------- Rename all ----------
  function renameAll() {
    setCustomNames({});
    showToast(`Renamed ${totalBoxes} hatchlings`);
  }

  // ---------- Per-hatchling rename ----------
  function startEdit(key, currentName) {
    setEditingKey(key);
    setEditingValue(currentName);
  }
  function commitEdit() {
    if (!editingKey) return;
    const v = (editingValue || '').trim().replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '_');
    setCustomNames(prev => {
      const next = { ...prev };
      if (v) next[editingKey] = v;
      else delete next[editingKey];
      return next;
    });
    setEditingKey(null); setEditingValue('');
  }
  function cancelEdit() { setEditingKey(null); setEditingValue(''); }

  // ---------- Drag reorder ----------
  function onDragStart(e, key) { dragKeyRef.current = key; e.dataTransfer.effectAllowed = 'move'; }
  function onDragOverItem(e) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }
  function onDropItem(e, targetKey) {
    e.preventDefault();
    const src = dragKeyRef.current;
    dragKeyRef.current = null;
    if (!src || src === targetKey) return;
    const keys = previews.map(p => p.key);
    const from = keys.indexOf(src); const to = keys.indexOf(targetKey);
    if (from < 0 || to < 0) return;
    const next = keys.slice();
    next.splice(from, 1);
    next.splice(to, 0, src);
    setPreviewOrder(next);
  }

  // ---------- Selection ----------
  function togglePreview(key) {
    setSelectedPreviews(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  }
  function toggleSelectAll() {
    if (selectedPreviews.size === previews.length) setSelectedPreviews(new Set());
    else setSelectedPreviews(new Set(previews.map(p => p.key)));
  }

  // ---------- Exports ----------
  function nestFolderName() {
    return safeName(activeNestName || 'cropasaurus-pack');
  }
  function toastForSaveResult(res, successMsg) {
    if (!res.ok) {
      if (res.cancelled) return; // user dismissed share sheet — no toast
      showToast('Download blocked. Check browser settings', 'error');
      return;
    }
    if (res.method === 'share') showToast('Opened share sheet. Pick "Save to Files".');
    else if (res.method === 'tab') showToast('Opened in new tab. Tap share to save it.');
    else showToast(successMsg);
  }

  async function exportZipAll() {
    if (previews.length === 0) return;
    setExporting(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder(nestFolderName());
      previews.forEach(p => folder.file(`${p.name}.png`, p.blob));
      const blob = await zip.generateAsync({ type: 'blob' });
      const res = await saveBlob(blob, `${nestFolderName()}.zip`);
      toastForSaveResult(res, 'Pack released as ZIP');
    } catch (e) {
      console.error(e); showToast('ZIP export failed', 'error');
    } finally { setExporting(false); }
  }
  async function exportZipSelected() {
    if (selectedPreviews.size === 0) return;
    setExporting(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder(nestFolderName());
      previews.forEach(p => { if (selectedPreviews.has(p.key)) folder.file(`${p.name}.png`, p.blob); });
      const blob = await zip.generateAsync({ type: 'blob' });
      const res = await saveBlob(blob, `${nestFolderName()}_selected.zip`);
      toastForSaveResult(res, `Released ${selectedPreviews.size} selected`);
    } catch (e) {
      console.error(e); showToast('ZIP export failed', 'error');
    } finally { setExporting(false); }
  }
  async function exportZipJPEG() {
    if (previews.length === 0) return;
    setExporting(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder(nestFolderName());
      for (const p of previews) {
        const jpgBlob = await canvasToJpegBlob(p.canvas, 0.92);
        folder.file(`${p.name}.jpg`, jpgBlob);
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const res = await saveBlob(blob, `${nestFolderName()}_jpeg.zip`);
      toastForSaveResult(res, 'JPEG pack released');
    } catch (e) {
      console.error(e); showToast('JPEG ZIP failed', 'error');
    } finally { setExporting(false); }
  }
  async function exportPDF() {
    if (previews.length === 0) return;
    setExporting(true);
    try {
      const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 36;
      const cols = 3, rows = 4;
      const cellW = (pageW - margin * 2) / cols;
      const cellH = (pageH - margin * 2) / rows;
      const stampPage = () => {
        pdf.setFont('courier', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor('#1f3d2b');
        pdf.text(
          'From Heritage Dahlias NZ  ·  cropasaurusrex.nz  ·  © 2026',
          pageW - margin,
          pageH - 14,
          { align: 'right' }
        );
      };
      stampPage();
      let i = 0;
      for (const p of previews) {
        const col = i % cols;
        const row = Math.floor(i / cols) % rows;
        if (i > 0 && i % (cols * rows) === 0) {
          pdf.addPage();
          stampPage();
        }
        const aspect = p.canvas.width / p.canvas.height;
        let w = cellW * 0.9, h = w / aspect;
        if (h > cellH * 0.78) { h = cellH * 0.78; w = h * aspect; }
        const x = margin + col * cellW + (cellW - w) / 2;
        const y = margin + row * cellH + (cellH - h) / 2 - 6;
        pdf.addImage(p.canvas.toDataURL('image/png'), 'PNG', x, y, w, h);
        pdf.setFont('courier', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor('#1f3d2b');
        pdf.text(p.name, margin + col * cellW + cellW / 2, margin + row * cellH + cellH - 8, { align: 'center', maxWidth: cellW - 8 });
        i++;
      }
      const blob = pdf.output('blob');
      const res = await saveBlob(blob, `${nestFolderName()}.pdf`);
      toastForSaveResult(res, 'PDF sheet released');
    } catch (e) {
      console.error(e); showToast('PDF export failed', 'error');
    } finally { setExporting(false); }
  }
  async function downloadAllPNGs() {
    if (previews.length === 0) return;
    if (previews.length === 1) {
      const res = await saveBlob(previews[0].blob, `${previews[0].name}.png`);
      toastForSaveResult(res, 'PNG downloaded');
      return;
    }
    setExporting(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder(nestFolderName());
      previews.forEach(p => folder.file(`${p.name}.png`, p.blob));
      const blob = await zip.generateAsync({ type: 'blob' });
      const res = await saveBlob(blob, `${nestFolderName()}_pngs.zip`);
      toastForSaveResult(res, `Downloaded ${previews.length} PNGs (zipped)`);
    } finally { setExporting(false); }
  }
  async function downloadOne(p) {
    const res = await saveBlob(p.blob, `${p.name}.png`);
    toastForSaveResult(res, 'PNG downloaded');
  }
  async function shareToPhone() {
    if (previews.length === 0) return;
    const canSharePromise = typeof navigator !== 'undefined' && navigator.share;
    if (!canSharePromise) {
      showToast('Sharing not supported here. Downloading ZIP', 'warn');
      return exportZipAll();
    }
    try {
      const files = previews.map(p => new File([p.blob], `${p.name}.png`, { type: 'image/png' }));
      if (navigator.canShare && navigator.canShare({ files })) {
        await navigator.share({
          files,
          title: nestFolderName(),
          text: `${previews.length} PNGs cropped by Cropasaurus Rex`,
        });
        showToast('Shared');
      } else {
        showToast('Files too large for share sheet. Using ZIP', 'warn');
        return exportZipAll();
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error(e);
        showToast('Share cancelled', 'warn');
      }
    }
  }

  // ============ NEST: save / load / delete ============
  async function handleCreateNest() {
    if (!newNestName.trim()) { showToast('Give your nest a name first', 'warn'); return; }
    if (sheets.length === 0) { showToast('Feed the Rex at least one sheet first', 'warn'); return; }
    setSavingNest(true);
    try {
      const id = newNestId();
      const sheetsPayload = await Promise.all(sheets.map(async s => ({
        name: s.name,
        blob: s.blob || s.file,
        boxes: s.boxes,
        bg: s.bg,
      })));
      await saveNest({
        id,
        name: newNestName.trim(),
        sheets: sheetsPayload,
        settings: { shape, bgRemove, tolerance, aggressive, centerInShape, prefix, startNum, padding },
        customNames,
        previewOrder,
      });
      setActiveNestId(id);
      setActiveNestName(newNestName.trim());
      setNewNestName('');
      await refreshNestList();
      showToast('Nest saved');
    } catch (e) {
      console.error(e);
      showToast('Failed to save nest', 'error');
    } finally { setSavingNest(false); }
  }
  async function handleUpdateNest() {
    if (!activeNestId) return;
    setSavingNest(true);
    try {
      const sheetsPayload = await Promise.all(sheets.map(async s => ({
        name: s.name,
        blob: s.blob || s.file,
        boxes: s.boxes,
        bg: s.bg,
      })));
      await saveNest({
        id: activeNestId,
        name: activeNestName,
        sheets: sheetsPayload,
        settings: { shape, bgRemove, tolerance, aggressive, centerInShape, prefix, startNum, padding },
        customNames,
        previewOrder,
      });
      await refreshNestList();
      showToast('Nest updated');
    } finally { setSavingNest(false); }
  }
  async function handleLoadNest(id) {
    try {
      const n = await loadNest(id);
      if (!n) return;
      const loadedSheets = await Promise.all((n.sheets || []).map(async sp => {
        const dataUrl = await readFileAsDataURL(sp.blob);
        const image = await loadImage(dataUrl);
        return {
          id: 's_' + Math.random().toString(36).slice(2, 9),
          name: sp.name, file: sp.blob, blob: sp.blob, dataUrl, image,
          boxes: sp.boxes || [], bg: sp.bg || null, detecting: false,
        };
      }));
      setSheets(loadedSheets);
      setCurrentIdx(0);
      setActiveNestId(n.id);
      setActiveNestName(n.name);
      if (n.settings) {
        setShape(n.settings.shape ?? 'rect');
        setBgRemove(n.settings.bgRemove ?? true);
        setTolerance(n.settings.tolerance ?? 30);
        setAggressive(n.settings.aggressive ?? false);
        setCenterInShape(n.settings.centerInShape ?? false);
        setPrefix(n.settings.prefix ?? 'icon');
        setStartNum(n.settings.startNum ?? 1);
        setPadding(n.settings.padding ?? 3);
      }
      setCustomNames(n.customNames || {});
      setPreviewOrder(n.previewOrder || []);
      setSelectedPreviews(new Set());
      showToast(`Opened nest "${n.name}"`);
    } catch (e) {
      console.error(e);
      showToast('Failed to load nest', 'error');
    }
  }
  async function handleDeleteNest(id, name) {
    if (!window.confirm(`Delete nest "${name}"? This cannot be undone.`)) return;
    await deleteNest(id);
    if (activeNestId === id) { setActiveNestId(null); setActiveNestName(''); }
    await refreshNestList();
    showToast('Nest deleted');
  }
  function leaveNest() {
    setActiveNestId(null); setActiveNestName('');
  }

  // ============ RENDER ============
  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: 'var(--cream)' }} data-testid="cropasaurus-app">

      {/* TOAST */}
      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 px-4 py-2 font-mono text-xs tracking-widest"
             style={{ background: 'var(--ink)', color: 'var(--cream)', border: '2px solid var(--amber)', boxShadow: '4px 4px 0 var(--amber)' }}
             data-testid="toast">
          {toast.msg.toUpperCase()}
        </div>
      )}

      {/* HEADER */}
      <header className="px-4 sm:px-6 md:px-10 pt-6 sm:pt-8 pb-6 max-w-[1200px] mx-auto">
        <div className="flex items-center gap-3 sm:gap-4 md:gap-6">
          <div className="rex-wiggle shrink-0" data-testid="rex-logo-header">
            <RexLogo size={56} className="sm:!w-[72px] md:!w-[88px]" />
          </div>
          <div className="flex-1 min-w-0">
            <h1
              className="font-display leading-[0.95]"
              style={{
                color: 'var(--ink)',
                fontSize: 'clamp(1.35rem, 6.2vw, 3rem)',
                wordBreak: 'normal',
                overflowWrap: 'normal',
                hyphens: 'none',
              }}
              data-testid="app-title"
            >
              CROPASAURUS REX
            </h1>
            <p className="font-mono text-[9px] sm:text-[11px] md:text-[13px] tracking-widest mt-1.5 sm:mt-2" style={{ color: 'var(--moss)' }}>
              FEED IT A PAGE · GET A PACK OF PNGS
              {activeNestName && <span className="ml-2" style={{ color: 'var(--amber-2)' }}>· NEST: {activeNestName.toUpperCase()}</span>}
            </p>
          </div>
          <button
            type="button"
            className="btn-ghost inline-flex items-center gap-2 shrink-0 text-xs sm:text-sm !px-3 sm:!px-4"
            onClick={resetAll}
            data-testid="reset-button"
            title="Reset everything"
          >
            <RotateCcw size={16} /> <span className="hidden sm:inline">RESET</span>
          </button>
        </div>
      </header>

      <div className="zigzag" />

      {/* INTRO */}
      <section className="px-5 md:px-10 pt-10 pb-6 max-w-[1200px] mx-auto">
        <h2 className="font-condensed text-4xl md:text-7xl leading-[0.95]" style={{ color: 'var(--ink)' }}>
          UPLOAD <span style={{ color: 'var(--amber)' }}>→</span> CROP <span style={{ color: 'var(--amber)' }}>→</span><br />
          RENAME <span style={{ color: 'var(--amber)' }}>→</span> EXPORT
        </h2>
        <p className="font-mono text-sm md:text-base mt-6 max-w-2xl tracking-wide" style={{ color: 'var(--moss)' }}>
          DROP A STICKER, BADGE, STAMP OR ICON SHEET. THE REX SLICES IT INTO INDIVIDUAL PNGS WITH TRANSPARENT
          BACKGROUNDS. READY FOR YOUR SITE, YOUR PHONE, OR YOUR STASH.
        </p>

        <div className="mt-10 border-2 p-5 md:p-7 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 bg-white"
             style={{ borderColor: 'var(--ink)' }} data-testid="the-herd-menu">
          <p className="col-span-2 md:col-span-3 font-display text-sm tracking-widest mb-2" style={{ color: 'var(--amber-2)' }}>
            THE HERD
          </p>
          {[
            ['01', 'FEED'], ['02', 'CHOMP'],
            ['03', 'NAME'], ['04', 'RELEASE'], ['05', 'NEST'],
          ].map(([num, label]) => (
            <div key={num} className="font-mono text-sm tracking-wider flex gap-3 items-center" style={{ color: 'var(--moss)' }}>
              <span className="opacity-60">{num}</span>
              <span className="opacity-30">·</span>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* STEP 01: FEED */}
      <section className="px-5 md:px-10 py-10 max-w-[1200px] mx-auto" data-testid="step-feed">
        <div className="flex items-center gap-3 mb-5">
          <span className="step-badge">01</span>
          <h3 className="font-display text-xl md:text-2xl tracking-wider" style={{ color: 'var(--ink)' }}>FEED THE REX</h3>
        </div>

        <div
          className={`dropzone ${isDragging ? 'dragging' : ''} p-10 md:p-16 text-center cursor-pointer relative`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          data-testid="dropzone"
        >
          <div className="flex flex-col items-center gap-4">
            <RexLogo size={90} />
            <h4 className="font-display text-2xl md:text-3xl" style={{ color: 'var(--ink)' }}>DROP IMAGE SHEETS</h4>
            <p className="font-mono text-xs md:text-sm tracking-widest" style={{ color: 'var(--moss)' }}>
              PNG · JPG · WEBP &nbsp;//&nbsp; UP TO {MAX_FILES} FILES &nbsp;//&nbsp; ≤ {MAX_SIZE_MB}MB EACH
            </p>
            <button
              type="button"
              className="btn-amber mt-3 inline-flex items-center gap-2"
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              data-testid="browse-files-button"
            >
              <Upload size={18} /> BROWSE FILES
            </button>
            <input
              ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp"
              multiple className="hidden" onChange={onFileInputChange} data-testid="file-input"
            />
          </div>
        </div>

        {sheets.length > 0 && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sheets-thumbnails">
            {sheets.map((s, i) => (
              <div key={s.id} className="relative border-2" style={{ borderColor: 'var(--ink)' }}>
                <img src={s.dataUrl} alt={s.name} className="block w-full h-28 object-cover" />
                <div className="absolute inset-x-0 bottom-0 px-2 py-1 text-[10px] font-mono truncate" style={{ background: 'var(--ink)', color: 'var(--cream)' }}>
                  {i + 1}. {s.name}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); removeSheet(i); }}
                  className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-white border-2"
                  style={{ borderColor: 'var(--ink)' }}
                  title="Remove" data-testid={`remove-sheet-${i}`}
                ><X size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* STEP 02: CHOMP */}
      {sheets.length > 0 && (
      <section className="px-5 md:px-10 py-10 max-w-[1200px] mx-auto" data-testid="step-chomp">
        <div className="flex items-center gap-3 mb-5">
          <span className="step-badge">02</span>
          <h3 className="font-display text-xl md:text-2xl tracking-wider" style={{ color: 'var(--ink)' }}>CHOMP DETECTION</h3>
        </div>

        <div className="flex flex-wrap gap-3 items-center mb-4">
          <button className="btn-ghost inline-flex items-center gap-1"
                  onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
                  disabled={currentIdx === 0} data-testid="prev-sheet">
            <ChevronLeft size={18} />
          </button>
          <div className="px-4 py-2 border-2 font-mono text-sm" style={{ borderColor: 'var(--ink)' }}>
            SHEET {currentIdx + 1} / {sheets.length}
          </div>
          <button className="btn-ghost inline-flex items-center gap-1"
                  onClick={() => setCurrentIdx(Math.min(sheets.length - 1, currentIdx + 1))}
                  disabled={currentIdx >= sheets.length - 1} data-testid="next-sheet">
            <ChevronRight size={18} />
          </button>
          <div className="ml-auto px-4 py-2 font-mono text-sm flex items-center gap-2"
               style={{ background: 'var(--ink)', color: 'var(--cream)' }} data-testid="icon-count">
            <Scissors size={16} /> {currentSheet?.boxes?.length || 0} ICONS
          </div>
          <div className="px-4 py-2 font-mono text-sm flex items-center gap-2"
               style={{ background: 'var(--amber)', color: 'var(--ink)', border: '2px solid var(--ink)' }}>
            TOTAL: {totalBoxes}
          </div>
        </div>

        <div className="border-2 p-2 md:p-3" style={{ borderColor: 'var(--ink)', background: '#fff' }}>
          {currentSheet?.detecting ? (
            <div className="aspect-[4/3] flex flex-col items-center justify-center gap-3 chomping" style={{ background: 'var(--cream-2)' }}>
              <RexLogo size={70} />
              <p className="font-display text-lg">CHOMP CHOMP...</p>
              <p className="font-mono text-xs opacity-60">DETECTING ICONS</p>
            </div>
          ) : (
            <BoxEditor
              image={currentSheet.image}
              boxes={currentSheet.boxes || []}
              onChange={updateBoxes}
              selectedId={selectedBoxId}
              onSelect={setSelectedBoxId}
            />
          )}
        </div>

        <div className="flex flex-wrap gap-3 mt-4">
          <button className="btn-ghost inline-flex items-center gap-2" onClick={addDefaultBox} data-testid="add-box">
            <Plus size={16} /> ADD BOX
          </button>
          <button className="btn-rust inline-flex items-center gap-2" onClick={deleteSelectedBox}
                  disabled={!selectedBoxId} data-testid="delete-box">
            <Trash2 size={16} /> DELETE
          </button>
          <button className="btn-ghost inline-flex items-center gap-2 ml-auto" onClick={redetectCurrent} data-testid="redetect">
            <RefreshCw size={16} /> RE-DETECT
          </button>
        </div>

        <p className="font-mono text-[11px] mt-3 opacity-60 border-2 inline-block px-3 py-2" style={{ borderColor: 'var(--ink)' }}>
          TIP: DRAG EMPTY AREA TO DRAW · CLICK A BOX TO SELECT · DRAG HANDLES TO RESIZE · WORKS ON TOUCH SCREENS
        </p>

        {/* APPLY-ALL + QUICK CROP PANEL — sits between editor and previews */}
        <div className="mt-5 border-2 p-4" style={{ borderColor: 'var(--ink)', background: 'var(--cream-2)' }} data-testid="quick-crop-panel">
          {sheets.length > 1 && (
            <button
              type="button"
              className="check-row mb-4"
              onClick={() => setApplyAllSheets(v => !v)}
              data-testid="apply-all-sheets"
            >
              <span className={`check-box ${applyAllSheets ? 'on' : ''}`}>{applyAllSheets ? '✓' : ''}</span>
              <span className="font-mono text-xs tracking-widest">
                CROP ALL {sheets.length} SHEETS THE SAME WAY (mirror this sheet&apos;s boxes)
              </span>
            </button>
          )}

          <div className="grid md:grid-cols-2 gap-5">
            {/* CROP TO EDGES (background removal) */}
            <div className="border-2 p-3" style={{ borderColor: 'rgba(31,61,43,0.35)' }}>
              <p className="font-condensed text-sm tracking-widest mb-3" style={{ color: 'var(--amber-2)' }}>
                CROP TO EDGES
              </p>
              <button type="button" className="check-row mb-2" onClick={() => setBgRemove(v => !v)} data-testid="quick-bgremove">
                <span className={`check-box ${bgRemove ? 'on' : ''}`}>{bgRemove ? '✓' : ''}</span>
                <span className="font-mono text-[11px] tracking-widest">Remove background, transparent PNG</span>
              </button>
              <button type="button" className="check-row mb-2" onClick={() => setAggressive(v => !v)} disabled={!bgRemove} data-testid="quick-aggressive">
                <span className={`check-box ${aggressive ? 'on' : ''}`}>{aggressive ? '✓' : ''}</span>
                <span className="font-mono text-[11px] tracking-widest" style={{ opacity: bgRemove ? 1 : 0.4 }}>
                  Aggressive (noisy backgrounds)
                </span>
              </button>
              <label className="block">
                <span className="font-mono text-[10px] opacity-60">TOLERANCE: {tolerance}</span>
                <input type="range" min="5" max="80" value={tolerance}
                       onChange={(e) => setTolerance(parseInt(e.target.value, 10))}
                       disabled={!bgRemove} className="w-full mt-1" />
              </label>
            </div>

            {/* CROP TO SHAPE */}
            <div className="border-2 p-3" style={{ borderColor: 'rgba(31,61,43,0.35)' }}>
              <p className="font-condensed text-sm tracking-widest mb-3" style={{ color: 'var(--amber-2)' }}>
                CROP TO SHAPE
              </p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[['rect','—'],['square','□'],['circle','○'],['rounded','▢'],['heritage','✿'],['dino','🦕'.replace('🦕','REX')]].map(([v,l]) => (
                  <button key={v} className={`chip !px-2 !py-1 !text-[10px] ${shape===v?'on':''}`}
                          onClick={() => setShape(v)} data-testid={`quick-shape-${v}`} title={v}>{l}</button>
                ))}
                {[3,4,5,6,7,8].map(n => (
                  <button key={n} className={`chip !px-2 !py-1 !text-[10px] ${shape===`polygon-${n}`?'on':''}`}
                          onClick={() => setShape(`polygon-${n}`)} data-testid={`quick-shape-polygon-${n}`}>{n}</button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                <button className={`chip !px-2 !py-1 !text-[10px] ${!centerInShape?'on':''}`} onClick={() => setCenterInShape(false)}>FILL</button>
                <button className={`chip !px-2 !py-1 !text-[10px] ${centerInShape?'on':''}`} onClick={() => setCenterInShape(true)}>CENTRE</button>
              </div>

              {/* BORDER controls */}
              <button type="button" className="check-row mb-2" onClick={() => setBorderEnabled(v => !v)} data-testid="border-toggle">
                <span className={`check-box ${borderEnabled ? 'on' : ''}`}>{borderEnabled ? '✓' : ''}</span>
                <span className="font-mono text-[11px] tracking-widest">Outline border in this shape</span>
              </button>
              {borderEnabled && (
                <div className="grid grid-cols-2 gap-2 ml-7">
                  <label className="flex items-center gap-2">
                    <input
                      type="color"
                      value={borderColor}
                      onChange={(e) => setBorderColor(e.target.value)}
                      data-testid="border-color"
                      style={{ width: 30, height: 24, border: '2px solid var(--ink)', padding: 0, background: 'transparent', cursor: 'pointer' }}
                    />
                    <span className="font-mono text-[10px] opacity-60">COLOUR</span>
                  </label>
                  <label>
                    <span className="font-mono text-[10px] opacity-60">THICKNESS: {borderThickness}px</span>
                    <input type="range" min="1" max="30" value={borderThickness}
                           onChange={(e) => setBorderThickness(parseInt(e.target.value, 10))}
                           data-testid="border-thickness"
                           className="w-full" />
                  </label>
                </div>
              )}

              {/* FRAME FILL controls — paints solid colour behind any transparent pixels */}
              <button type="button" className="check-row mt-3 mb-2" onClick={() => setBgFillEnabled(v => !v)} data-testid="bgfill-toggle">
                <span className={`check-box ${bgFillEnabled ? 'on' : ''}`}>{bgFillEnabled ? '✓' : ''}</span>
                <span className="font-mono text-[11px] tracking-widest">Fill frame with colour (replace transparent)</span>
              </button>
              {bgFillEnabled && (
                <div className="ml-7 flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2">
                    <input
                      type="color"
                      value={bgFillColor}
                      onChange={(e) => setBgFillColor(e.target.value)}
                      data-testid="bgfill-color"
                      style={{ width: 30, height: 24, border: '2px solid var(--ink)', padding: 0, background: 'transparent', cursor: 'pointer' }}
                    />
                    <span className="font-mono text-[10px] opacity-60">COLOUR</span>
                  </label>
                  <div className="flex flex-wrap gap-1">
                    {['#1f3d2b','#ffffff','#000000','#f5e9d4','#d97706','#a0c4a8'].map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setBgFillColor(c)}
                        data-testid={`bgfill-swatch-${c.replace('#','')}`}
                        title={c}
                        aria-label={`Fill color ${c}`}
                        style={{
                          width: 20, height: 20, background: c, cursor: 'pointer',
                          border: bgFillColor.toLowerCase() === c ? '2px solid var(--ink)' : '1px solid rgba(31,61,43,0.4)',
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* LIVE PREVIEW STRIP for the CURRENT SHEET */}
        {(() => {
          const currentPreviews = currentSheet
            ? previews.filter(p => p.sheetId === currentSheet.id)
            : [];
          return (
            <div className="mt-6 border-2 p-4" style={{ borderColor: 'var(--ink)', background: 'var(--cream-2)' }} data-testid="chomp-previews">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <p className="font-condensed text-base tracking-widest" style={{ color: 'var(--ink)' }}>
                  LIVE CROP PREVIEW
                </p>
                <span className="font-mono text-[11px] opacity-60">
                  {currentPreviews.length} icon{currentPreviews.length === 1 ? '' : 's'} on this sheet · live updates as you tweak boxes &amp; settings
                </span>
              </div>
              {currentPreviews.length === 0 ? (
                <p className="font-mono text-xs opacity-50 text-center py-5">
                  {building ? 'CHOMPING...' : 'Add a box above to see its cropped preview here.'}
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {currentPreviews.map((p) => {
                    const idx = previews.findIndex(x => x.key === p.key);
                    return (
                      <div key={p.key} className="border-2" style={{ borderColor: 'var(--ink)' }}>
                        <div className="checker aspect-square flex items-center justify-center relative">
                          <img src={p.url} alt={p.name} className="max-w-full max-h-full object-contain" />
                          <button
                            onClick={() => downloadOne(p)}
                            className="absolute bottom-1 right-1 w-7 h-7 flex items-center justify-center transition-transform hover:scale-110"
                            style={{ background: 'var(--ink)', color: 'var(--amber)', border: '2px solid var(--amber)' }}
                            title="Download PNG"
                            data-testid={`chomp-download-${p.key}`}
                          ><Download size={12} /></button>
                          <span className="absolute top-1 left-1 px-2 py-0.5 font-mono text-[10px]"
                                style={{ background: 'var(--ink)', color: 'var(--amber)' }}>
                            #{idx + 1}
                          </span>
                        </div>
                        {/* Always-visible inline rename */}
                        <div className="px-2 py-2" style={{ background: 'var(--ink)' }}>
                          <input
                            type="text"
                            value={customNames[p.key] ?? p.autoName}
                            onChange={(e) => {
                              const v = e.target.value.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '_');
                              setCustomNames(prev => {
                                const next = { ...prev };
                                if (v && v !== p.autoName) next[p.key] = v;
                                else delete next[p.key];
                                return next;
                              });
                            }}
                            className="w-full bg-transparent outline-none font-mono text-[10px] border-b"
                            style={{ color: 'var(--amber)', borderColor: 'rgba(240,180,48,0.4)' }}
                            data-testid={`chomp-name-${p.key}`}
                            spellCheck={false}
                          />
                          <p className="font-mono text-[9px] mt-1 opacity-50" style={{ color: 'var(--cream)' }}>.png</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </section>
      )}

      {/* STEP 03: NAME (was 05/04 — Smart Cropping step removed since all controls are now inline in CHOMP) */}
      {sheets.length > 0 && (
      <section className="px-5 md:px-10 py-10 max-w-[1200px] mx-auto" data-testid="step-name">
        <div className="flex items-center gap-3 mb-5">
          <span className="step-badge">03</span>
          <h3 className="font-display text-xl md:text-2xl tracking-wider" style={{ color: 'var(--ink)' }}>NAME YOUR HATCHLINGS</h3>
        </div>
        <div className="border-2 bg-white" style={{ borderColor: 'var(--ink)' }}>
          <div className="grid md:grid-cols-3 gap-0">
            <div className="p-5 border-b-2 md:border-b-0 md:border-r-2" style={{ borderColor: 'var(--ink)' }}>
              <p className="font-mono text-xs tracking-widest mb-2 opacity-70">NAME PREFIX</p>
              <input type="text" value={prefix} onChange={(e) => setPrefix(e.target.value)}
                     className="input-rex" placeholder="heritage-stickers" data-testid="prefix-input" />
            </div>
            <div className="p-5 border-b-2 md:border-b-0 md:border-r-2" style={{ borderColor: 'var(--ink)' }}>
              <p className="font-mono text-xs tracking-widest mb-2 opacity-70">START NUMBER</p>
              <input type="number" min="0" value={startNum}
                     onChange={(e) => setStartNum(Math.max(0, parseInt(e.target.value || '0', 10)))}
                     className="input-rex" data-testid="start-num-input" />
            </div>
            <div className="p-5">
              <p className="font-mono text-xs tracking-widest mb-2 opacity-70">DIGIT PADDING</p>
              <input type="number" min="1" max="6" value={padding}
                     onChange={(e) => setPadding(Math.max(1, Math.min(6, parseInt(e.target.value || '1', 10))))}
                     className="input-rex" data-testid="padding-input" />
            </div>
          </div>

          <div className="border-t-2 p-5" style={{ borderColor: 'var(--ink)' }}>
            <p className="font-mono text-xs tracking-widest mb-3 opacity-70">PREVIEW</p>
            <div className="flex flex-wrap gap-2" data-testid="name-preview-chips">
              {previews.slice(0, 3).map(p => (
                <span key={p.key} className="border-2 px-3 py-1 font-mono text-xs" style={{ borderColor: 'var(--ink)' }}>
                  {p.name}.png
                </span>
              ))}
              {previews.length > 3 && (
                <span className="border-2 px-3 py-1 font-mono text-xs" style={{ borderColor: 'var(--ink)' }}>...</span>
              )}
              {previews.length === 0 && (
                <span className="font-mono text-xs opacity-60">Add boxes to see preview names</span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <button className="btn-amber inline-flex items-center gap-2" onClick={renameAll}
                  disabled={totalBoxes === 0} data-testid="rename-all">
            <Wand2 size={18} /> RENAME ALL ({totalBoxes})
          </button>
          <p className="font-mono text-[11px] opacity-70 self-center">
            Tip: rename one at a time directly under each cropped image above (or in Release the Herd below).
          </p>
        </div>
      </section>
      )}

      {/* STEP 04: RELEASE — dark green (was 05; HATCH removed) */}
      {sheets.length > 0 && (
      <section className="mt-6" data-testid="step-release" style={{ background: 'var(--ink)', color: 'var(--cream)' }}>
        <div className="px-5 md:px-10 py-12 max-w-[1200px] mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <span className="step-badge" style={{ background: 'var(--amber)', color: 'var(--ink)', borderColor: 'var(--amber)' }}>04</span>
            <h3 className="font-display text-xl md:text-2xl tracking-wider" style={{ color: 'var(--cream)' }}>RELEASE THE HERD</h3>
          </div>
          <p className="font-mono text-sm tracking-widest mb-8" style={{ color: 'var(--cream-2)' }}>
            {previews.length} TOTAL · NEST: {(activeNestName || 'ROOT').toUpperCase()}
          </p>

          {/* Action buttons (stacked, full-width feel) */}
          <div className="flex flex-col gap-4 mb-8" data-testid="export-buttons">
            <button
              className="btn-amber inline-flex items-center justify-center gap-3 w-full text-lg py-4"
              onClick={exportZipAll}
              disabled={previews.length === 0 || exporting}
              data-testid="export-zip-all"
            >
              <Archive size={20} /> EXPORT ALL AS ZIP ({previews.length})
            </button>

            <button
              className="inline-flex items-center justify-center gap-3 w-full py-4 font-bold tracking-widest text-base"
              style={{
                background: selectedPreviews.size === 0 ? 'rgba(242, 234, 208, 0.18)' : 'var(--cream)',
                color: selectedPreviews.size === 0 ? 'rgba(242, 234, 208, 0.55)' : 'var(--ink)',
                border: '2px solid var(--cream)',
                cursor: selectedPreviews.size === 0 ? 'not-allowed' : 'pointer',
                textTransform: 'uppercase',
              }}
              onClick={exportZipSelected}
              disabled={selectedPreviews.size === 0 || exporting}
              data-testid="export-zip-selected"
            >
              <Archive size={20} /> ZIP SELECTED ({selectedPreviews.size})
            </button>

            <button
              className="inline-flex items-center justify-center gap-3 w-full py-4 font-bold tracking-widest text-base"
              style={{ border: '2px solid var(--cream)', color: 'var(--cream)', background: 'transparent', textTransform: 'uppercase' }}
              onClick={downloadAllPNGs}
              disabled={previews.length === 0 || exporting}
              data-testid="download-pngs"
            >
              <Download size={20} /> DOWNLOAD PNGS
            </button>

            <button
              className="inline-flex items-center justify-center gap-3 w-full py-4 font-bold tracking-widest text-base"
              style={{ border: '2px solid var(--cream)', color: 'var(--cream)', background: 'transparent', textTransform: 'uppercase' }}
              onClick={shareToPhone}
              disabled={previews.length === 0 || exporting}
              data-testid="share-phone"
            >
              <Share2 size={20} /> SHARE / SAVE TO PHONE
            </button>

            <div className="flex flex-wrap gap-3">
              <button className="btn-ghost inline-flex items-center gap-2"
                      style={{ borderColor: 'var(--cream)', color: 'var(--cream)' }}
                      onClick={exportZipJPEG} disabled={previews.length === 0 || exporting}
                      data-testid="export-zip-jpeg">
                <FileImage size={16} /> ZIP · JPEG
              </button>
              <button className="btn-ghost inline-flex items-center gap-2"
                      style={{ borderColor: 'var(--cream)', color: 'var(--cream)' }}
                      onClick={exportPDF} disabled={previews.length === 0 || exporting}
                      data-testid="export-pdf">
                <FileText size={16} /> PDF SHEET
              </button>
              {(building || exporting) && (
                <span className="font-mono text-xs px-3 py-2 inline-flex items-center gap-2"
                      style={{ background: 'var(--amber)', color: 'var(--ink)' }}>
                  <RefreshCw size={14} className="animate-spin" />
                  {building ? 'CHOMPING…' : 'EXPORTING…'}
                </span>
              )}
            </div>
          </div>

          <p className="font-mono text-[11px] mb-6 max-w-2xl tracking-widest" style={{ color: 'var(--cream-2)' }}>
            TIP: ON IPHONE &amp; ANDROID, &ldquo;SHARE / SAVE TO PHONE&rdquo; OPENS THE SYSTEM SHEET SO YOU CAN SAVE DIRECTLY
            TO PHOTOS / FILES.
          </p>

          {/* Preview grid */}
          <div className="flex items-center justify-between mb-3">
            <p className="font-mono text-xs tracking-widest opacity-80">PREVIEW &amp; SELECT · DRAG TO REORDER</p>
            <button onClick={toggleSelectAll}
                    className="font-mono text-[11px] tracking-widest underline"
                    style={{ color: 'var(--amber)' }}
                    data-testid="toggle-select-all">
              {selectedPreviews.size === previews.length && previews.length > 0 ? 'UNSELECT ALL' : 'SELECT ALL'}
            </button>
          </div>

          <div className="border-2 p-3 md:p-4" style={{ borderColor: 'var(--amber)', background: 'rgba(242,234,208,0.06)' }}>
            {previews.length === 0 ? (
              <p className="font-mono text-xs opacity-60 text-center py-8" style={{ color: 'var(--cream-2)' }}>
                {building ? 'BUILDING PREVIEWS...' : 'ADD BOXES IN STEP 02 TO SEE PREVIEWS'}
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="preview-grid">
                {previews.map((p) => {
                  const isSel = selectedPreviews.has(p.key);
                  const isEditing = editingKey === p.key;
                  return (
                    <div
                      key={p.key}
                      className="border-2 relative group"
                      style={{ borderColor: isSel ? 'var(--amber)' : 'var(--cream)' }}
                      draggable={!isEditing}
                      onDragStart={(e) => onDragStart(e, p.key)}
                      onDragOver={onDragOverItem}
                      onDrop={(e) => onDropItem(e, p.key)}
                      data-testid={`preview-item-${p.key}`}
                    >
                      <div className="checker aspect-square flex items-center justify-center relative">
                        <img src={p.url} alt={p.name} className="max-w-full max-h-full object-contain" />
                        {/* Select checkbox */}
                        <button
                          onClick={() => togglePreview(p.key)}
                          className="absolute top-1 left-1 w-6 h-6 flex items-center justify-center"
                          style={{
                            background: isSel ? 'var(--amber)' : 'rgba(255,255,255,0.85)',
                            border: '2px solid var(--ink)',
                          }}
                          data-testid={`select-${p.key}`}
                          title={isSel ? 'Unselect' : 'Select'}
                        >
                          {isSel && <Check size={12} style={{ color: 'var(--ink)' }} />}
                        </button>
                        {/* Drag handle */}
                        <span className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center cursor-grab"
                              style={{ background: 'rgba(31,61,43,0.8)', color: 'var(--cream)' }} title="Drag to reorder">
                          <GripVertical size={12} />
                        </span>
                        {/* Download (always visible — touch-friendly) */}
                        <button
                          onClick={() => downloadOne(p)}
                          className="absolute bottom-1 right-1 w-7 h-7 flex items-center justify-center transition-transform hover:scale-110"
                          style={{ background: 'var(--ink)', color: 'var(--amber)', border: '2px solid var(--amber)' }}
                          title="Download PNG"
                          data-testid={`download-${p.key}`}
                        ><Download size={12} /></button>
                      </div>
                      <div className="px-2 py-1 font-mono text-[10px] flex items-center gap-1" style={{ background: 'var(--ink)', color: 'var(--cream)' }}>
                        {isEditing ? (
                          <>
                            <input
                              autoFocus
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); else if (e.key === 'Escape') cancelEdit(); }}
                              onBlur={commitEdit}
                              className="flex-1 bg-transparent outline-none font-mono text-[10px]"
                              style={{ color: 'var(--amber)', borderBottom: '1px solid var(--amber)' }}
                              data-testid={`name-edit-${p.key}`}
                            />
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(p.key, p.name)}
                              className="flex-1 truncate text-left"
                              title="Click to rename"
                              data-testid={`name-${p.key}`}
                            >{p.name}.png</button>
                            <Pencil size={10} className="opacity-50" />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
      )}

      {/* STEP 05: NEST */}
      <section className="px-5 md:px-10 py-10 max-w-[1200px] mx-auto" data-testid="step-nest">
        <div className="flex items-center gap-3 mb-5">
          <span className="step-badge">05</span>
          <h3 className="font-display text-xl md:text-2xl tracking-wider" style={{ color: 'var(--ink)' }}>
            CREATE NEST
          </h3>
        </div>

        <div className="border-2 bg-white" style={{ borderColor: 'var(--ink)' }}>
          {/* create form */}
          <div className="p-5 border-b-2" style={{ borderColor: 'var(--ink)' }}>
            <p className="font-mono text-xs tracking-widest mb-3 opacity-70">SAVE CURRENT WORK AS A NEST</p>
            <div className="flex flex-col md:flex-row gap-3">
              <input
                type="text"
                value={newNestName}
                onChange={(e) => setNewNestName(e.target.value)}
                className="input-rex flex-1"
                placeholder="Bloom Club Icons"
                data-testid="new-nest-name"
              />
              <button
                className="btn-ink inline-flex items-center gap-2"
                onClick={handleCreateNest}
                disabled={savingNest || sheets.length === 0}
                data-testid="create-nest-btn"
              >
                <FolderPlus size={18} /> {savingNest ? 'SAVING…' : 'CREATE NEST'}
              </button>
              {activeNestId && (
                <button
                  className="btn-ghost inline-flex items-center gap-2"
                  onClick={handleUpdateNest}
                  disabled={savingNest}
                  title="Update the currently active nest with latest changes"
                  data-testid="update-nest-btn"
                >
                  <Save size={16} /> UPDATE CURRENT
                </button>
              )}
            </div>
            <p className="font-mono text-[11px] mt-3 opacity-60">
              A nest stores ALL sheets, boxes, names &amp; settings locally on this device. Nothing is uploaded.
            </p>
          </div>

          {/* No-nest / root row */}
          <button
            type="button"
            className="w-full p-4 flex items-center gap-3 border-b-2 hover:bg-cream-2 text-left transition-colors"
            style={{ borderColor: 'var(--ink)', background: !activeNestId ? 'var(--cream-2)' : 'transparent' }}
            onClick={leaveNest}
            data-testid="no-nest-row"
          >
            <Folder size={20} style={{ color: 'var(--ink)' }} />
            <span className="font-mono text-sm tracking-widest">NO NEST (ROOT)</span>
            {!activeNestId && <Check size={16} className="ml-auto" style={{ color: 'var(--amber-2)' }} />}
          </button>

          {/* saved nests */}
          {nestList.length === 0 && (
            <p className="px-4 py-5 font-mono text-[11px] opacity-60">No saved nests yet. Create one above.</p>
          )}
          {nestList.map(n => (
            <div key={n.id} className="relative border-b-2 last:border-b-0" style={{ borderColor: 'var(--ink)' }}>
              <button
                type="button"
                className="w-full p-4 flex items-center gap-3 text-left transition-colors hover:bg-amber-50"
                style={{ background: activeNestId === n.id ? 'var(--cream-2)' : 'transparent' }}
                onClick={() => handleLoadNest(n.id)}
                data-testid={`nest-${n.id}`}
              >
                <Folder size={20} style={{ color: 'var(--ink)' }} />
                <span className="font-mono text-sm tracking-widest flex-1 truncate">{n.name.toUpperCase()}</span>
                <span className="font-mono text-[10px] opacity-60 hidden md:inline">
                  {n.sheetCount} sheet{n.sheetCount === 1 ? '' : 's'} · {n.boxCount} icon{n.boxCount === 1 ? '' : 's'}
                </span>
                {activeNestId === n.id && <Check size={16} style={{ color: 'var(--amber-2)' }} />}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleDeleteNest(n.id, n.name); }}
                className="absolute -top-1 -right-1 w-8 h-8 flex items-center justify-center"
                style={{ background: 'var(--rust)', color: 'var(--paper)', border: '2px solid var(--ink)' }}
                title={`Delete ${n.name}`}
                data-testid={`delete-nest-${n.id}`}
              ><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </section>

      {/* FOOTER — "Created by a Legend" */}
      <footer className="px-5 md:px-10 py-14 max-w-[1200px] mx-auto" data-testid="footer">
        <h2 className="font-condensed text-4xl md:text-6xl mb-6" style={{ color: 'var(--ink)' }}>
          CREATED BY A LEGEND
        </h2>
        <div className="flex items-start gap-5 mb-8">
          <div className="opacity-90 shrink-0">
            <RexLogo size={56} />
          </div>
          <p className="font-mono text-sm md:text-base tracking-wide max-w-xl" style={{ color: 'var(--moss)' }}>
            CROPASAURUS REX · FEED A PAGE · GET A PACK OF PNGS
          </p>
        </div>
        <div className="w-full py-5 px-6 flex items-center justify-center mb-4"
             style={{ background: 'var(--ink)', color: 'var(--paper)' }}>
          <p className="font-display text-lg md:text-xl tracking-widest text-center">
            PROPERTY OF HERITAGE DAHLIAS NZ LIMITED
          </p>
        </div>
        <p className="font-mono text-xs md:text-sm tracking-widest mb-6" style={{ color: 'var(--moss)' }}>
          ©{' '}
          <span
            onClick={handleSecretClick}
            onDoubleClick={handleSecretClick}
            role="button"
            tabIndex={-1}
            aria-label="2026"
            data-testid="secret-lab-trigger"
            style={{ cursor: 'default', userSelect: 'none' }}
          >2026</span>
          {' '}· ALL RIGHTS RESERVED · CROPASAURUSREX.NZ
        </p>

        {/* Sister-site + terms button row */}
        <div className="flex flex-wrap gap-3 mb-10" data-testid="footer-links">
          <a
            href="https://heritagedahlias.nz"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 font-mono text-[11px] tracking-widest"
            style={{ border: '2px solid var(--ink)', color: 'var(--ink)' }}
            data-testid="footer-link-heritagedahlias"
          >
            HERITAGEDAHLIAS.NZ <ExternalLink size={12} />
          </a>
          <a
            href="https://heritagebloomclub.nz"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 font-mono text-[11px] tracking-widest"
            style={{ border: '2px solid var(--ink)', color: 'var(--ink)' }}
            data-testid="footer-link-bloomclub"
          >
            HERITAGEBLOOMCLUB.NZ <ExternalLink size={12} />
          </a>
          <button
            type="button"
            onClick={() => setTermsOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 font-mono text-[11px] tracking-widest"
            style={{ background: 'var(--ink)', color: 'var(--amber)' }}
            data-testid="open-terms-app"
          >
            TERMS &amp; POLICIES
          </button>
        </div>

        {/* PRIVACY & IP PROTECTION */}
        <div className="border-2 p-5 md:p-7 mt-4" style={{ borderColor: 'var(--ink)', background: 'var(--cream-2)' }} data-testid="legal">
          <h3 className="font-display text-xl md:text-2xl tracking-wider mb-5" style={{ color: 'var(--ink)' }}>
            PRIVACY &amp; PROTECTION
          </h3>

          <div className="grid md:grid-cols-2 gap-6 md:gap-10">
            <div>
              <p className="font-condensed text-base tracking-widest mb-3" style={{ color: 'var(--amber-2)' }}>
                YOUR PRIVACY
              </p>
              <ul className="font-mono text-[12px] md:text-[13px] leading-relaxed space-y-2" style={{ color: 'var(--ink)' }}>
                <li>· All image processing happens locally in your browser.</li>
                <li>· Nothing is uploaded. Nothing is stored on a server.</li>
                <li>· Your saved Nests live only in your own device storage.</li>
                <li>· No tracking. No analytics. No cookies. No third party scripts.</li>
                <li>· The images you process remain entirely yours.</li>
              </ul>
            </div>

            <div>
              <p className="font-condensed text-base tracking-widest mb-3" style={{ color: 'var(--amber-2)' }}>
                INTELLECTUAL PROPERTY
              </p>
              <ul className="font-mono text-[12px] md:text-[13px] leading-relaxed space-y-2" style={{ color: 'var(--ink)' }}>
                <li>· The name &ldquo;Cropasaurus Rex&rdquo;, the tortoise mark, the Heritage Crop shape, the Dino Crop shape and the overall visual identity (cream / dark green / amber palette, zine layout, the step naming of Feed, Chomp, Hatch, Name, Nest, Release) are the original creation and intellectual property of Heritage Dahlias NZ Limited.</li>
                <li>· All trademarks, copy, design elements and source code are the property of Heritage Dahlias NZ Limited.</li>
                <li>· Unauthorised reproduction, redistribution, framing, scraping, or derivative works of the Cropasaurus Rex application, its branding, or any associated visual assets is strictly prohibited.</li>
                <li>· Use of this tool does not transfer any rights in the application or its branding.</li>
              </ul>
            </div>
          </div>

          <p className="font-mono text-[11px] tracking-widest mt-6 pt-5 border-t" style={{ color: 'var(--moss)', borderColor: 'rgba(31,61,43,0.25)' }}>
            For licensing, permissions, or commercial enquiries, contact <a href="https://heritagedahlias.nz" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--amber-2)' }}>heritagedahlias.nz</a>.
            {' '}Read the <button type="button" onClick={() => setTermsOpen(true)} className="underline" style={{ color: 'var(--amber-2)' }} data-testid="open-terms-inline">full Terms, Privacy &amp; Design Protection</button>.
          </p>
        </div>
      </footer>

      {/* TERMS MODAL */}
      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </div>
  );
}

function safeName(name) {
  return (name || 'cropasaurus-pack').trim().replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '-') || 'cropasaurus-pack';
}
